/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from 'react';
import { DATA_CY_MAIN_LAYOUT } from 'global/MainLayout/__tests__/constant';
import styled, { css } from 'styled-components';
import color from '../_style/color';
import device from '../_style/device';
import { Flex } from '../common';

const navigationWidthByDevice = css`
  width: calc((100%-45px) / 2);

  @media ${device.mobileS} {
    width: calc((100%-45px) / 3);
  }

  @media ${device.mobileL} {
    width: calc((100%-45px) / 4);
  }

  @media ${device.tablet} {
    width: calc((100%-45px) / 6);
  }

  @media ${device.laptop} {
    width: calc((100%-45px) / 9);
  }
`;

const Navigation = css`
  font-family: 'ProximaNovaRg';
  text-decoration: none;
  color: ${color.dimGray};
  cursor: pointer;
  border-left: 1px solid ${color.gainsboro};

  :first-child {
    border-left: unset;
  }

  :focus,
  :active {
    color: ${color.primary};
    background: ${color.aliceBlue};
    :hover {
      color: ${color.primary};
    }
  }
  :hover {
    color: ${color.primary};
  }
`;

const NavBarWebWrapper = styled.div`
  background: ${color.light};
  display: flex;
  border-top: 1px solid ${color.lowgray};
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${color.gainsboro};
`;

const HorizontalNavigations = styled(Flex)`
  position: relative;
  height: 40px;
  max-height: 40px;
  box-sizing: border-box;
  overflow: hidden;
  flex-wrap: ${({ isNavigationBlock }) =>
    isNavigationBlock ? 'unset' : 'wrap'};
  width: ${({ isNavigationBlock }) => (isNavigationBlock ? '100%' : 'auto')};

  & > a {
    ${Navigation};
    width: ${({ isNavigationBlock }) =>
      isNavigationBlock ? '100%' : navigationWidthByDevice};
  }
`;

const VerticalNavigations = styled.div`
  width: 200px;
  position: absolute;
  right: 0;
  height: 0px;
  opacity: 0;
  visibility: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid ${color.lowgray};
  border-top: none;
  padding: 10px 0;
  transition: all 0.2s ease-in-out;
  z-index: 5;

  a:nth-child(-n + ${(props) => props.elementToShow}) {
    display: none;
  }

  & > a {
    ${Navigation};
    border-left: unset;
    text-align: right;
    width: 100%;
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      background-color: ${color.light};
      height: auto;
      opacity: 1;
      visibility: visible;
    `}
`;

const NavMoreLogoWrapper = styled.div`
  border-width: ${({ isOpen }) => (isOpen ? '0px 1px' : '0px')};
  border-color: ${color.lowgray};
  border-style: solid;
  border-left: 1px solid ${color.gainsboro};
  cursor: pointer;

  ::after {
    content: '';
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    width: 50px;
    height: 1px;
    background-color: ${color.light};
    position: absolute;
    bottom: -1px;
    z-index: 2;
  }
`;

const Relative = styled.div`
  position: relative;
`;

const NavMoreWrapper = ({ ExpandLogo, children, elementToShow }) => {
  const [hideModal, setModal] = useState(true);
  const navMoreRef = useRef();

  const onNavMoreClick = (e) => {
    e?.stopPropagation();
    if (navMoreRef.current.contains(e.target)) {
      return setModal(!hideModal);
    }
    return setModal(true);
  };

  useEffect(() => {
    document.addEventListener('click', onNavMoreClick);
    return () => {
      document.removeEventListener('click', onNavMoreClick);
    };
  });

  return (
    <Relative>
      <NavMoreLogoWrapper
        ref={navMoreRef}
        isOpen={!hideModal}
        onClick={(e) => onNavMoreClick(e)}
      >
        {elementToShow !== children.length && <ExpandLogo />}
      </NavMoreLogoWrapper>
      <VerticalNavigations isOpen={!hideModal} elementToShow={elementToShow}>
        {children}
      </VerticalNavigations>
    </Relative>
  );
};

export const NavMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 40px;

  & > span {
    width: 5px;
    height: 5px;
    margin: 2px;
    border-radius: 50%;
    border: 1px solid ${color.highgray};
    border-color: ${({ isOpen }) => (isOpen ? color.primary : '')};
  }
`;

const NavMoreLogo = () => {
  return (
    <NavMore>
      <span />
      <span />
      <span />
    </NavMore>
  );
};

const NavBarComponent = ({
  className,
  children,
  expandNavLogo = () => <div />,
}) => {
  const [moreNavElement, setMoreNavElement] = useState([]);
  const navHorizontalRef = useRef();
  const ExpandNavLogo = expandNavLogo;

  useEffect(() => {
    const navigation = navHorizontalRef.current.children;
    const moreNavigation = [...navigation]
      .filter((e) => e.offsetTop === 0)
      .map((e) => e.innerText);
    setMoreNavElement(moreNavigation);
  }, []);

  return (
    <NavBarWebWrapper className={className}>
      <HorizontalNavigations
        isNavigationBlock={moreNavElement.length === children.length}
        ref={navHorizontalRef}
      >
        {children}
      </HorizontalNavigations>
      <NavMoreWrapper
        ExpandLogo={ExpandNavLogo}
        elementToShow={moreNavElement.length}
      >
        {children}
      </NavMoreWrapper>
    </NavBarWebWrapper>
  );
};

export default { NavMoreLogo, NavBarComponent };
